import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Upload, CheckCircle2, Lock, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, uploadFile, fileUrl } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { aadhaarValid, COUNTRIES } from "@/lib/site";
import { Button } from "@/components/ui/button";
import SuggestInput from "@/components/SuggestInput";

const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const inputCls =
  "w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] text-ink outline-none focus:border-brand";
const DRAFT_KEY = "vhrp_reg_draft";
// Auto-insert "/" as the user types digits -> DD/MM/YYYY
const formatDob = (v) => {
  const d = String(v).replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
};

export default function Register() {
  const [params] = useSearchParams();
  const tier = Number(params.get("tier")) || 21;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();

  const [form, setForm] = useState({
    full_name: user?.name || "",
    father_name: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    assembly: "",
    social_link: "",
    phone: "",
    email: user?.email || "",
    dob: "",
    blood: "O+",
    aadhaar: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const designation = tier === 101 ? t("desigGeneral") : t("desigMember");
  const [dialCode, setDialCode] = useState("+91");
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [frontPath, setFrontPath] = useState(null);
  const [backPath, setBackPath] = useState(null);
  const [frontBusy, setFrontBusy] = useState(false);
  const [backBusy, setBackBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bootDone, setBootDone] = useState(false);
  const [isExisting, setIsExisting] = useState(false);

  const photoRef = useRef(null);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await api("/membership/me");
        if (m.member) {
          setIsExisting(true);
          const mm = m.member;
          setForm((f) => ({
            ...f,
            full_name: mm.full_name || f.full_name,
            father_name: mm.father_name || "",
            address: mm.address || "",
            city: mm.city || "",
            state: mm.state || "",
            country: mm.country || "India",
            assembly: mm.assembly || "",
            social_link: mm.social_link || "",
            email: mm.email || f.email,
            dob: mm.dob || "",
            blood: mm.blood_group || "O+",
            aadhaar: mm.aadhaar || "",
          }));
          const ph = String(mm.phone || "");
          if (ph.startsWith("+") && ph.includes(" ")) {
            const [dc, ...rest] = ph.split(" ");
            setDialCode(dc);
            set("phone", rest.join(" "));
          } else if (ph) {
            set("phone", ph);
          }
          if (mm.photo_path) {
            setPhotoPath(mm.photo_path);
            setPhotoPreview(await fileUrl(mm.photo_path));
          }
          if (mm.aadhaar_front_path) setFrontPath(mm.aadhaar_front_path);
          if (mm.aadhaar_back_path) setBackPath(mm.aadhaar_back_path);
        } else {
          // No existing membership — restore a locally saved draft so the user can
          // continue a registration they started earlier.
          try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
              const d = JSON.parse(raw);
              if (d.form) setForm((f) => ({ ...f, ...d.form }));
              if (d.dialCode) setDialCode(d.dialCode);
              if (d.frontPath) setFrontPath(d.frontPath);
              if (d.backPath) setBackPath(d.backPath);
              if (d.photoPath) {
                setPhotoPath(d.photoPath);
                setPhotoPreview(await fileUrl(d.photoPath));
              }
            }
          } catch (e) { console.error("draft restore failed", e); }
        }
      } catch (e) { console.error("Failed to prefill membership form", e); }
      finally { setBootDone(true); }
    })();
  }, []);

  // Autosave a draft of a NEW registration (Aadhaar number is intentionally NOT
  // persisted in browser localStorage for privacy; the user re-enters it).
  useEffect(() => {
    if (!bootDone || isExisting) return;
    const { aadhaar, ...safeForm } = form;
    const draft = { form: safeForm, dialCode, photoPath, frontPath, backPath };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* quota/private mode */ }
  }, [bootDone, isExisting, form, dialCode, photoPath, frontPath, backPath]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const { path } = await uploadFile(file, "/upload/member-photo");
      setPhotoPath(path);
      toast.success(t("photoVerifying"));
    } catch (err) {
      toast.error(err.message || t("photoRejected"));
      setPhotoPreview(null);
      setPhotoPath(null);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleAadhaar = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setBusyS = side === "front" ? setFrontBusy : setBackBusy;
    setBusyS(true);
    try {
      const { path } = await uploadFile(file, "/upload/photo");
      if (side === "front") setFrontPath(path);
      else setBackPath(path);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusyS(false);
    }
  };

  const submit = async () => {
    if (!form.full_name.trim() || !form.father_name.trim() || !form.address.trim() ||
        !form.phone.trim() || !form.dob.trim() || !form.city.trim() || !form.state.trim() || !form.country.trim()) {
      toast.error(t("regFillReq"));
      return;
    }
    const isIndia = /^\s*india\s*$/i.test(form.country);
    if (isIndia && !form.assembly.trim()) {
      toast.error(t("regAssemblyReq"));
      return;
    }
    if (!/^\d{12}$/.test(form.aadhaar.replace(/\D/g, "")) || !aadhaarValid(form.aadhaar)) {
      toast.error(t("regAadhaarInvalid"));
      return;
    }
    if (!photoPath) {
      toast.error(t("photoRequired"));
      return;
    }
    if (!frontPath || !backPath) {
      toast.error(t("aadhaarPhotosReq"));
      return;
    }
    setBusy(true);
    try {
      await api("/membership/register", {
        method: "POST",
        body: JSON.stringify({
          full_name: form.full_name,
          father_name: form.father_name,
          address: form.address,
          city: form.city.trim(),
          state: form.state.trim(),
          country: form.country.trim() || "India",
          assembly: isIndia ? form.assembly.trim() : null,
          social_link: form.social_link.trim() || null,
          phone: `${dialCode} ${form.phone.trim()}`,
          email: form.email,
          dob: form.dob,
          designation,
          blood_group: form.blood,
          aadhaar: form.aadhaar.replace(/\D/g, ""),
          aadhaar_front_path: frontPath,
          aadhaar_back_path: backPath,
          photo_path: photoPath,
          tier,
        }),
      });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      navigate(`/payment?tier=${tier}`);
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const isIndia = /^\s*india\s*$/i.test(form.country);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-brand">
        <ArrowLeft size={16} /> {t("back")}
      </button>
      <h1 className="text-2xl font-black text-ink">{t("regTitle")}</h1>
      <p className="mt-1 text-sm font-semibold text-brand">
        {t("regTierLabel")}: ₹{tier} {t("perYear")}
      </p>

      <div className="mt-8 space-y-6 rounded-3xl border border-line bg-white p-6 sm:p-8">
        {/* Photo */}
        <div className="flex items-center gap-5">
          <input ref={photoRef} type="file" accept="image/*" hidden onChange={handlePhoto} data-testid="photo-input" />
          <button
            data-testid="photo-picker"
            onClick={() => photoRef.current?.click()}
            disabled={photoBusy}
            className="relative flex h-32 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-brand/40 bg-brand-tint"
          >
            {photoPreview ? (
              <img src={photoPreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-brand">
                <Camera size={26} />
                <span className="text-xs font-semibold">{t("regPhotoAdd")}</span>
              </div>
            )}
            {photoBusy && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="animate-spin text-white" />
              </div>
            )}
          </button>
          <div className="flex-1 text-sm text-ink-muted">
            <p>{t("regPhotoHint")}</p>
            {photoBusy && <p className="mt-2 font-bold text-brand">{t("photoVerifying")}</p>}
          </div>
        </div>

        <Field label={t("regFullName")}><input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="f-fullname" /></Field>
        <Field label={t("regFather")}><input className={inputCls} value={form.father_name} onChange={(e) => set("father_name", e.target.value)} data-testid="f-father" /></Field>
        <Field label={t("regAddress")}><textarea className={`${inputCls} min-h-[80px]`} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="f-address" /></Field>

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label={t("regCity")}>
            <SuggestInput value={form.city} onChange={(v) => set("city", v)} endpoint="/geo/cities" params={{ state: form.state }} resultKey="cities" testID="f-city" />
          </Field>
          <Field label={t("regState")}>
            <SuggestInput value={form.state} onChange={(v) => { set("state", v); set("assembly", ""); }} endpoint="/geo/states" resultKey="states" testID="f-state" />
          </Field>
        </div>

        <Field label={`${t("regCountry")} *`}><input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="f-country" /></Field>

        {isIndia && (
          <Field label={t("regAssembly")}>
            <SuggestInput value={form.assembly} onChange={(v) => set("assembly", v)} endpoint="/geo/assemblies" params={{ state: form.state }} resultKey="assemblies" placeholder={t("regAssemblyPh")} testID="f-assembly" />
          </Field>
        )}

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label={t("regPhone")}>
            <div className="flex">
              <select
                data-testid="dial-code-select"
                value={dialCode}
                onChange={(e) => {
                  setDialCode(e.target.value);
                  const c = COUNTRIES.find((x) => x.code === e.target.value);
                  if (c) set("country", c.name);
                }}
                className="rounded-l-lg border border-r-0 border-line bg-[#F2EBE1] px-2 text-sm font-semibold outline-none"
              >
                {COUNTRIES.map((c, i) => (
                  <option key={`${c.code}-${i}`} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input className={`${inputCls} rounded-l-none`} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="f-phone" inputMode="tel" />
            </div>
          </Field>
          <Field label={t("regDob")}>
            <input className={inputCls} value={form.dob} onChange={(e) => set("dob", formatDob(e.target.value))} placeholder="DD/MM/YYYY" maxLength={10} inputMode="numeric" data-testid="f-dob" />
          </Field>
        </div>

        <Field label={t("regEmail")}><input className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="f-email" type="email" /></Field>

        <Field label={t("regDesignation")}>
          <div className="flex items-center justify-between rounded-lg border border-line bg-[#F2EBE1] px-3 py-2.5" data-testid="f-designation">
            <span className="font-bold text-ink">{designation}</span>
            <Lock size={14} className="text-ink-muted" />
          </div>
        </Field>

        <Field label={t("regBlood")}>
          <div className="flex flex-wrap gap-2">
            {BLOOD.map((b) => (
              <button
                key={b}
                data-testid={`blood-${b}`}
                onClick={() => set("blood", b)}
                className={`h-10 min-w-[52px] rounded-full border px-3 text-sm font-bold transition-colors ${form.blood === b ? "border-brand bg-brand text-white" : "border-line bg-white text-ink"}`}
              >
                {b}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t("regAadhaar")}>
          <input className={inputCls} value={form.aadhaar} maxLength={12} inputMode="numeric" onChange={(e) => set("aadhaar", e.target.value.replace(/\D/g, ""))} data-testid="f-aadhaar" />
        </Field>

        {/* Aadhaar photos */}
        <div className="grid gap-6 sm:grid-cols-2">
          <AadhaarBox label={t("aadhaarFront")} testid="f-aadhaar-front" done={!!frontPath} busy={frontBusy} inputRef={frontRef} onChange={(e) => handleAadhaar(e, "front")} />
          <AadhaarBox label={t("aadhaarBack")} testid="f-aadhaar-back" done={!!backPath} busy={backBusy} inputRef={backRef} onChange={(e) => handleAadhaar(e, "back")} />
        </div>
        <p className="-mt-3 text-xs text-ink-muted">🔒 {t("aadhaarAdminOnly")}</p>

        <Field label={t("regSocial")}>
          <input className={inputCls} value={form.social_link} onChange={(e) => set("social_link", e.target.value)} placeholder={t("regSocialPh")} data-testid="f-social" />
        </Field>

        <Button data-testid="proceed-payment" onClick={submit} disabled={busy} className="h-12 w-full rounded-full bg-brand text-base font-bold hover:bg-brand-dark">
          {busy ? <Loader2 className="animate-spin" /> : (<>{t("regPay")} ₹{tier} <ArrowRight size={18} className="ml-1" /></>)}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-ink-soft">{label}</label>
      {children}
    </div>
  );
}

function AadhaarBox({ label, testid, done, busy, inputRef, onChange }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-ink-soft">{label}</label>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onChange} />
      <button
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="relative flex h-24 w-full items-center justify-center rounded-lg border-2 border-dashed border-borderStrong bg-white"
        style={{ borderColor: "#C2B4A3" }}
      >
        {busy ? (
          <Loader2 className="animate-spin text-brand" />
        ) : done ? (
          <CheckCircle2 size={28} className="text-[#2E7D32]" />
        ) : (
          <Upload size={24} className="text-brand" />
        )}
      </button>
    </div>
  );
}
